import { ComponentType, FC, memo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useMediaQuery from '@/hooks/use-media-query'
import { ChevronLeft } from '@/components/icons'
import { colors, breakpoints, fontWeights } from '@/styles'
import { BreadcrumbList, BackButton, BreadcrumbAnchor, getBackEntry } from './elements'
import { BreadcrumbProps, AnchorProps, BreadcrumbLink } from './types'
import separatorArrow from './breadcrumb-separator.svg?url'

const BreadcrumbNav = styled.nav`
  font-size: 13px;
  color: ${colors.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: 0.27px;
  line-height: 18px;
  margin: 0;
  --hover-color: ${colors.secondary};
`

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    &:after {
      content: url(\"${separatorArrow}\");
      padding: 0 10px;
      height: 16px;
      width: 16px;
    }
  }
`

const isComponent = (node: BreadcrumbLink['label']): node is ComponentType<Nothing> => {
  return typeof node === 'function'
}

const Breadcrumbs: FC<BreadcrumbProps> = ({ items, className }) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)

  return (
    <BreadcrumbNav className={className}>
      {displayMobile ? (
        <BackButton to={getBackEntry(items)}>
          <ChevronLeft width={20} height={20} />
          {t('common.back', 'Back')}
        </BackButton>
      ) : (
        <BreadcrumbList>
          {items.map(({ to, label: Label }, i) => {
            const props: AnchorProps = !!to
              ? { to, isActive: () => false }
              : { to: '#', tabIndex: -1, isActive: () => true }
            const toRender = isComponent(Label) ? <Label /> : Label

            return (
              <BreadcrumbItem key={`breadcrumb_no_${i}`}>
                <BreadcrumbAnchor {...props} aria-current="page">
                  {toRender}
                </BreadcrumbAnchor>
              </BreadcrumbItem>
            )
          })}
        </BreadcrumbList>
      )}
    </BreadcrumbNav>
  )
}

export default memo(Breadcrumbs)
